import {
  PropsWithChildren,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState
} from 'react';
import { Platform, StyleProp, StyleSheet, ViewProps, ViewStyle } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { Portal } from 'common/src/components/portal';
import Animated, {
  AnimatedStyleProp,
  measure,
  runOnJS,
  useAnimatedRef,
  useAnimatedStyle,
  useSharedValue,
  withTiming
} from 'react-native-reanimated';
import { create } from 'zustand';
import { ScaleContentContext, ScaleFactorContext } from 'common/src/theme/scaling';
import { readonlyMapEntry } from 'common/src/utils/collections';
import { throwError } from '../../utils/flowControl';
import { getPlayer } from 'common/src/utils/Audio';

export type ID = string | number;

type DroppableDetails = {
  payload?: unknown;
  ref: RefObject<Animated.View>;
  scaleFactor: number;
  onEntered: (event: { draggable: DraggableEventData }) => void;
  onExited: (event: { draggable: DraggableEventData }) => void;
  onDropInto: (event: { draggable: DraggableEventData }) => void | boolean;
};

type DragAndDropState = {
  channelMap: ReadonlyMap<
    ID,
    {
      droppables: ReadonlyMap<ID, DroppableDetails>;
    }
  >;

  registerDroppable: (channel: ID, id: ID, data: DroppableDetails) => void;
  unregisterDroppable: (channel: ID, id: ID) => void;
};

const useDragAndDropStore = create<DragAndDropState>(set => ({
  channelMap: new Map(),

  registerDroppable: (channel: ID, id: ID, data: DroppableDetails) =>
    set(old => {
      const channelMap = readonlyMapEntry(old.channelMap, channel, channelData => {
        if (channelData === undefined) {
          channelData = { droppables: new Map() };
        }

        const newDroppables = readonlyMapEntry(channelData.droppables, id, () => data);
        let newChannelData;
        if (channelData.droppables === newDroppables) {
          newChannelData = channelData;
        } else {
          newChannelData = { ...channelData, droppables: newDroppables };
        }
        return newChannelData;
      });
      return { channelMap };
    }),

  unregisterDroppable: (channel: ID, id: ID) =>
    set(old => {
      const channelMap = readonlyMapEntry(old.channelMap, channel, channelData => {
        if (channelData === undefined) {
          return undefined;
        }

        const newDroppables = readonlyMapEntry(channelData.droppables, id, () => undefined);
        let newChannelData;
        if (channelData.droppables === newDroppables) {
          newChannelData = channelData;
        } else {
          newChannelData = { ...channelData, droppables: newDroppables };
        }
        return newChannelData;
      });
      return { channelMap };
    })
}));

type Measurements = { width: number; height: number; x: number; y: number };

function calculateWhichDroppableHoveredOver(
  measuredDroppables: [ID, Measurements][],
  event: { absoluteX: number; absoluteY: number }
): ID | undefined {
  'worklet';
  for (const [id, { x, y, width, height }] of measuredDroppables) {
    if (
      x < event.absoluteX &&
      event.absoluteX < x + width &&
      y < event.absoluteY &&
      event.absoluteY < y + height
    ) {
      return id;
    }
  }
  return undefined;
}

export type DroppableEventData = {
  id: ID;
  payload: unknown;
};

export type DraggableEventData = {
  id: ID;
  payload: unknown;
};

export type AnimationState = {
  home: boolean;
  animation: 'none' | 'dragging' | 'snapback';
  droppableEntered?: ID;
};

export type DraggableProps = {
  /** ID used to identify the draggable in callbacks. Defaults to auto-generated ID. */
  id?: ID;
  /** Used to toggle off dragging. Defaults to true. */
  dragEnabled?: boolean;
  /** Specifies which droppables this draggable can interact with. Defaults to ''. */
  channel?: ID;
  /** Style of the wrapping view. Can be an animated style (using react-native-reanimated). */
  style?: StyleProp<ViewStyle>;
  /** Any arbitrary data associated to this draggable that you might want to send to the droppable. */
  payload?: unknown;
  /**
   * Whether to snapback (i.e. animate the hovered draggable back to home) at the end of an animation by default.
   * Defaults to true.
   */
  snapback?: boolean;

  // Styles - warning: these don't seem to be working particularly well!
  idleStyle?: StyleProp<ViewStyle>;
  draggingStyle?: StyleProp<ViewStyle>;
  draggingHoverStyle?: StyleProp<ViewStyle>;

  // Advanced style control
  styleWorklet?: (event: AnimationState) => AnimatedStyleProp<ViewStyle>;
  hoverStyleWorklet?: (event: AnimationState) => AnimatedStyleProp<ViewStyle>;

  // Callbacks. Boolean return indicates whether the draggable should snap back (leave void for default).
  onDragStart?: () => void;
  onDragEnter?: (event: { droppable: DroppableEventData }) => void;
  onDragExit?: (event: { droppable: DroppableEventData }) => void;
  onDragEnd?: (event: { droppable?: DroppableEventData }) => void | boolean;
  onDragCancel?: () => void | boolean;
  onDragFinalized?: (didSnapback: boolean) => void;
};

/**
 * Wrapper around a component to make it draggable. This is an {@link Animated.View} with special properties:
 *
 * 1. The view can be dragged around the screen while draggable = true.
 * 2. If the view is hovered over a droppable in the same channel, the droppable is informed of this.
 * 3. If the view is dropped over a droppable in the same channel, the droppable is informed of this.
 *
 * In cases 3 and 4, the droppable gets a reference to this draggable's ID and payload.
 */
export function Draggable({
  id: propId,
  children,
  dragEnabled = true,
  channel: channelProp = '',
  style,
  payload,
  snapback = true,
  idleStyle,
  draggingStyle,
  draggingHoverStyle,
  styleWorklet = () => {
    'worklet';
    return {};
  },
  hoverStyleWorklet = () => {
    'worklet';
    return {};
  },
  onDragStart = () => {
    /* do nothing */
  },
  onDragEnter = () => {
    /* do nothing */
  },
  onDragExit = () => {
    /* do nothing */
  },
  onDragEnd = () => {
    /* do nothing */
  },
  onDragCancel = () => {
    /* do nothing */
  },
  onDragFinalized = () => {
    /* do nothing */
  },
  ...props
}: Omit<ViewProps, 'id'> & PropsWithChildren<DraggableProps>) {
  const randomId = useId();
  const id = propId ?? randomId;

  // Scope the channel to the nearest ScaleContent.
  const scaleContentId = useContext(ScaleContentContext).scaleContentId;
  const channel = `${scaleContentId}_${channelProp}`;

  /** Measurements of interesting things, made at the beginning of the gesture. */
  const measurements = useSharedValue<{
    measuredDraggable?: Measurements;
    measuredDroppables: [ID, Measurements][];
    portalTopLeft?: { x: number; y: number };
  }>({ measuredDroppables: [] });

  const state = useSharedValue<AnimationState>({ home: true, animation: 'none' });
  const dragOffset = useSharedValue<{ x: number; y: number }>({ x: 0, y: 0 });
  const snapbackProgress = useSharedValue<number | undefined>(undefined);

  const droppables = useDragAndDropStore(state => state.channelMap.get(channel)?.droppables);
  const scaleFactor = useContext(ScaleFactorContext);

  const portalRef = useContext(ScaleContentContext).ref;
  const draggableRef = useAnimatedRef<Animated.View>();

  ////
  // Functions to call on JS thread when key moments happen
  ////

  const eventOnDragStart = useCallback(() => {
    const player = getPlayer();
    player.playSound('pickup');
    onDragStart();
  }, [onDragStart]);

  const eventOnDragEnter = useCallback(
    (droppableId: ID) => {
      const droppableData = droppables?.get(droppableId);
      if (droppableData === undefined) {
        return;
      }
      onDragEnter({ droppable: { id: droppableId, payload: droppableData.payload } });
      droppableData.onEntered({ draggable: { id, payload } });
    },
    [droppables, id, onDragEnter, payload]
  );

  const eventOnDragExit = useCallback(
    (droppableId: ID) => {
      const droppableData = droppables?.get(droppableId);
      if (droppableData === undefined) {
        return;
      }
      onDragExit({ droppable: { id: droppableId, payload: droppableData.payload } });
      droppableData.onExited({ draggable: { id, payload } });
    },
    [droppables, id, onDragExit, payload]
  );

  const finishDrag = useCallback(
    (doSnapback: boolean) => {
      if (doSnapback) {
        state.value = { home: false, animation: 'snapback' };
        snapbackProgress.value = 1;
        const onFinish = () => {
          'worklet';
          snapbackProgress.value = undefined;
          state.value = { home: true, animation: 'none' };
          dragOffset.value = { x: 0, y: 0 };
          runOnJS(onDragFinalized)(true);
        };
        snapbackProgress.value = withTiming(0, {}, onFinish);
      } else {
        state.value = { home: true, animation: 'none' };
        dragOffset.value = { x: 0, y: 0 };
        onDragFinalized(false);
      }
    },
    [dragOffset, onDragFinalized, snapbackProgress, state]
  );

  const eventOnDragEnd = useCallback(
    (droppableId: ID | undefined) => {
      const droppableData = droppableId === undefined ? undefined : droppables?.get(droppableId);
      // By default, apply snapback if it wasn't dropped anywhere.
      let snapbackOverride: boolean = droppableId !== undefined ? false : snapback;
      if (droppableId === undefined || droppableData === undefined) {
        const player = getPlayer();
        player.playSound('delete');
        snapbackOverride = onDragEnd({}) ?? snapbackOverride;
      } else {
        const player = getPlayer();
        player.playSound('drop');
        snapbackOverride =
          droppableData.onDropInto({ draggable: { id, payload } }) ?? snapbackOverride;
        snapbackOverride =
          onDragEnd({ droppable: { id: droppableId, payload: droppableData.payload } }) ??
          snapbackOverride;
      }
      finishDrag(snapbackOverride);
    },
    [droppables, finishDrag, id, onDragEnd, payload, snapback]
  );

  const eventOnDragCancel = useCallback(() => {
    const snapbackOverride = onDragCancel() ?? snapback;
    finishDrag(snapbackOverride);
  }, [finishDrag, onDragCancel, snapback]);

  ////
  // Measuring
  ////

  const droppablesRefAndScaleFactor = useMemo(
    () =>
      droppables === undefined
        ? undefined
        : [...droppables].map(([id, { ref, scaleFactor }]) => [id, { ref, scaleFactor }] as const),
    [droppables]
  );
  const measureEverything = useCallback(() => {
    'worklet';
    // Measure the top-left of the nearest portal ancestor.
    if (portalRef === undefined) {
      measurements.value = { ...measurements.value, portalTopLeft: { x: 0, y: 0 } };
    } else {
      if (Platform.OS === 'web') {
        // On web, all values are unscaled.
        // So we need to scale to get real dimens.
        portalRef.current?.measure((_x, _y, _width, _height, pageX, pageY) => {
          measurements.value = {
            ...measurements.value,
            portalTopLeft: { x: pageX * scaleFactor, y: pageY * scaleFactor }
          };
        });
      } else {
        try {
          const { pageX, pageY } = measure(portalRef) ?? throwError('returned null');
          measurements.value = { ...measurements.value, portalTopLeft: { x: pageX, y: pageY } };
        } catch (e) {
          console.log('Failed to measure portal', e);
        }
      }
    }

    // Measure the initial position of the draggable.
    if (Platform.OS === 'web') {
      draggableRef.current?.measure((_x, _y, width, height, pageX, pageY) => {
        // On web, all values are unscaled.
        // So we need to scale to get real dimens.
        measurements.value = {
          ...measurements.value,
          measuredDraggable: {
            width: width * scaleFactor,
            height: height * scaleFactor,
            x: pageX * scaleFactor,
            y: pageY * scaleFactor
          }
        };
      });
    } else {
      try {
        const { width, height, pageX, pageY } =
          measure(draggableRef) ?? throwError('returned null');
        measurements.value = {
          ...measurements.value,
          measuredDraggable: { width, height, x: pageX, y: pageY }
        };
      } catch (e) {
        console.log('Failed to measure draggable', e);
      }
    }

    // Measure the drop zones at the time the gesture starts.
    measurements.value = { ...measurements.value, measuredDroppables: [] };
    droppablesRefAndScaleFactor !== undefined &&
      droppablesRefAndScaleFactor.forEach(([droppableId, droppable]) => {
        if (Platform.OS === 'web') {
          droppable.ref.current?.measure((_x, _y, width, height, pageX, pageY) => {
            // On web, all values are unscaled.
            // So we need to scale to get real dimens.
            const measurementsInScreen = {
              width: width * droppable.scaleFactor,
              height: height * droppable.scaleFactor,
              x: pageX * droppable.scaleFactor,
              y: pageY * droppable.scaleFactor
            };
            measurements.value = {
              ...measurements.value,
              measuredDroppables: [
                ...measurements.value.measuredDroppables,
                [droppableId, measurementsInScreen]
              ]
            };
          });
        } else {
          try {
            const { width, height, pageX, pageY } =
              measure(droppable.ref) ?? throwError('returned null');
            const measurementsInScreen = { width, height, x: pageX, y: pageY };
            measurements.value = {
              ...measurements.value,
              measuredDroppables: [
                ...measurements.value.measuredDroppables,
                [droppableId, measurementsInScreen]
              ]
            };
          } catch (e) {
            console.log('Failed to measure droppable region', e);
          }
        }
      });
  }, [draggableRef, droppablesRefAndScaleFactor, measurements, portalRef, scaleFactor]);

  ////
  // Gesture
  ////

  /** Where the gesture begins (before it's active), record the offset relative to the draggable. */
  const beginOffset = useSharedValue<{ x: number; y: number } | null>(null);

  const panGesture = useMemo(
    () =>
      Gesture.Pan()
        .onBegin(event => {
          let { x, y } = event;
          if (Platform.OS !== 'web') {
            // On native these values are pre-scaling-transformation, so scale to get real values
            x *= scaleFactor;
            y *= scaleFactor;
          }

          state.value = { home: false, animation: 'dragging' };
          runOnJS(eventOnDragStart)();

          measureEverything();
          dragOffset.value = { x: 0, y: 0 };
          beginOffset.value = { x, y };
        })

        .onUpdate(event => {
          let { x, y } = event;
          if (Platform.OS !== 'web') {
            // On native these values are pre-scaling-transformation, so scale to get real values
            x *= scaleFactor;
            y *= scaleFactor;
          }

          const oldDroppableHoveredOver = state.value.droppableEntered;

          const droppableHoveredOver = measurements.value.measuredDraggable
            ? calculateWhichDroppableHoveredOver(measurements.value.measuredDroppables, {
                absoluteX: x + measurements.value.measuredDraggable.x,
                absoluteY: y + measurements.value.measuredDraggable.y
              })
            : undefined;

          if (oldDroppableHoveredOver !== droppableHoveredOver) {
            // Inform droppables of hovering changes.
            oldDroppableHoveredOver !== undefined &&
              runOnJS(eventOnDragExit)(oldDroppableHoveredOver);
            droppableHoveredOver !== undefined && runOnJS(eventOnDragEnter)(droppableHoveredOver);
          }

          // Update animation state
          state.value = {
            home: false,
            animation: 'dragging',
            droppableEntered: droppableHoveredOver
          };

          // Calculate the dragOffset from the x, y values and where the gesture began.
          // translateX and translateY aren't quite right as their calculated from when the first
          // update occurred, which might not be the same point
          dragOffset.value =
            beginOffset.value === null
              ? // This case should never happen, but it's a reasonable fallback
                { x: 0, y: 0 }
              : {
                  x: x - beginOffset.value.x,
                  y: y - beginOffset.value.y
                };
        })

        .onFinalize((_event, success) => {
          beginOffset.value = null;
          const droppableId = state.value.droppableEntered;

          // Workaround: on web, short gestures seem to be treated as failures a lot, even though they don't seem
          // like they should be failures. Overide here to always count it as a success.
          success = success || Platform.OS === 'web';

          // The following callbacks set the animation state.
          if (success) {
            // Place in whichever droppable was currently hovered, if any.
            runOnJS(eventOnDragEnd)(droppableId);
          } else {
            runOnJS(eventOnDragCancel)();
          }
        }),
    [
      beginOffset,
      dragOffset,
      eventOnDragCancel,
      eventOnDragEnd,
      eventOnDragEnter,
      eventOnDragExit,
      eventOnDragStart,
      measureEverything,
      measurements.value.measuredDraggable,
      measurements.value.measuredDroppables,
      scaleFactor,
      state
    ]
  );

  // If dragEnabled is false, use an empty gesture.
  // Note that we don't use the `.enabled` method on the previous pan gesture, because we don't want to recreate it
  // when `dragEnabled` changes, as that has led to weird bugs.
  const gestureToUse = useMemo(
    () => (dragEnabled ? panGesture : Gesture.Pan()),
    [dragEnabled, panGesture]
  );

  ////
  // Styles
  ///

  const idleStyleFlat = useMemo(() => StyleSheet.flatten(idleStyle) ?? {}, [idleStyle]);
  const draggingStyleFlat = useMemo(() => StyleSheet.flatten(draggingStyle) ?? {}, [draggingStyle]);
  const draggingStyleAnimated = useAnimatedStyle(() => {
    return state.value.home ? idleStyleFlat : draggingStyleFlat;
  }, [draggingStyleFlat, idleStyleFlat, state]);
  const draggingHoverStyleFlat = useMemo(
    () => StyleSheet.flatten(draggingHoverStyle),
    [draggingHoverStyle]
  );
  const draggingHoverStyleAnimated = useAnimatedStyle(() => {
    return state.value.home ? {} : draggingHoverStyleFlat;
  }, [draggingHoverStyleFlat, state]);
  const hoverOffsetStyle = useAnimatedStyle(() => {
    if (
      !state.value.home &&
      measurements.value.portalTopLeft !== undefined &&
      measurements.value.measuredDraggable !== undefined
    ) {
      const snap = snapbackProgress.value ?? 1;

      // Styles for this view need to use unscaled dimensions
      return {
        opacity: 1,
        left:
          (measurements.value.measuredDraggable.x - measurements.value.portalTopLeft.x) /
          scaleFactor,
        top:
          (measurements.value.measuredDraggable.y - measurements.value.portalTopLeft.y) /
          scaleFactor,
        transform: [
          { translateX: (snap * dragOffset.value.x) / scaleFactor },
          { translateY: (snap * dragOffset.value.y) / scaleFactor }
        ]
      };
    } else {
      return { opacity: 0 };
    }
  }, [dragOffset, measurements, scaleFactor, snapbackProgress, state]);
  const styleWorkletStyle = useAnimatedStyle(() => {
    return styleWorklet(state.value);
  }, [state, styleWorklet]);
  const hoverStyleWorkletStyle = useAnimatedStyle(() => {
    return hoverStyleWorklet(state.value);
  }, [state, hoverStyleWorklet]);

  return (
    <>
      <GestureDetector gesture={gestureToUse}>
        <Animated.View
          style={[style, draggingStyleAnimated, styleWorkletStyle]}
          ref={draggableRef}
          {...props}
        >
          {children}
        </Animated.View>
      </GestureDetector>
      <Portal>
        <Animated.View
          style={[style, draggingHoverStyleAnimated, hoverStyleWorkletStyle, hoverOffsetStyle]}
          pointerEvents="none"
        >
          {children}
        </Animated.View>
      </Portal>
    </>
  );
}

export type DroppableProps = {
  /** ID used to identify the droppable in callbacks. Defaults to auto-generated ID. */
  id?: ID;
  /** Specifies which draggables this droppable can interact with. Defaults to ''. */
  channel?: ID;
  /** Data received by the draggable, when dropped. */
  payload?: unknown;
  /** Style of the wrapping view. Can be an animated style (using react-native-reanimated). */
  style?: StyleProp<ViewStyle>;

  // Conditional styles - convenience for simple style changes. If you want more control, provide an AnimatedStyle to
  // the style prop, and animate it based on events.
  enteredStyle?: StyleProp<ViewStyle>;

  // Callbacks. Boolean return indicates whether the draggable should snap back (leave void for default).
  onEntered?: (event: { draggable: DraggableEventData }) => void;
  onExited?: (event: { draggable: DraggableEventData }) => void;
  onDropInto?: (event: { draggable: DraggableEventData }) => void | boolean;
};

/**
 * Wrapper around a component to make it droppable - i.e. a valid drop target for a {@link Draggable}.
 * This is an {@link Animated.View} with special properties:
 *
 * 1. The view is a valid drop zone for a draggable of the same channel.
 * 2. The view is informed when a draggable that's being dragged is hovering over it or dropped into it.
 */
export function Droppable({
  id: propId,
  channel: channelProp = '',
  style,
  payload,
  enteredStyle,
  onEntered: onEnteredProp = () => {
    /* do nothing */
  },
  onExited: onExitedProp = () => {
    /* do nothing */
  },
  onDropInto: onDropIntoProp = () => {
    /* do nothing */
  },
  children,
  ...props
}: Omit<ViewProps, 'id'> & PropsWithChildren<DroppableProps>) {
  const randomId = useId();
  const id = propId ?? randomId;

  // Scope the channel to the nearest ScaleContent.
  const scaleContentId = useContext(ScaleContentContext).scaleContentId;
  const channel = `${scaleContentId}_${channelProp}`;

  const scaleFactor = useContext(ScaleFactorContext);
  const ref = useAnimatedRef<Animated.View>();

  const [isEntered, setEntered] = useState(false);
  const onEntered = useCallback(
    (event: { draggable: DraggableEventData }) => {
      setEntered(true);
      onEnteredProp(event);
    },
    [onEnteredProp]
  );
  const onExited = useCallback(
    (event: { draggable: DraggableEventData }) => {
      setEntered(false);
      onExitedProp(event);
    },
    [onExitedProp]
  );
  const onDropInto = useCallback(
    (event: { draggable: DraggableEventData }) => {
      setEntered(false);
      onDropIntoProp(event);
    },
    [onDropIntoProp]
  );

  const registerDroppable = useDragAndDropStore(state => state.registerDroppable);
  const unregisterDroppable = useDragAndDropStore(state => state.unregisterDroppable);

  useEffect(() => {
    registerDroppable(channel, id, { payload, ref, scaleFactor, onEntered, onExited, onDropInto });
    return () => unregisterDroppable(channel, id);
  }, [
    channel,
    id,
    onDropInto,
    onEntered,
    onExited,
    payload,
    ref,
    registerDroppable,
    scaleFactor,
    unregisterDroppable
  ]);

  return (
    <Animated.View style={[style, isEntered && enteredStyle]} ref={ref} {...props}>
      {children}
    </Animated.View>
  );
}
